import React, { useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useScript } from 'hooks/useScript';
import PropTypes from 'prop-types';
import { EditorState, Modifier } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { showErrorNotification } from 'utils/error-handlers';
import UPLOAD_WIDGET_CONFIG from 'config/cloudinary-video-upload-widget';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'draft-js/dist/Draft.css';
// ---
import { uploadToCloudinary } from 'utils/helpers';
import { customizeFeature } from './utils/customize-feature';
import { withPinyin } from './with-pinyin';
import { withAudio } from './with-audio';
import {
  registerController,
  unregisterController,
  forEachController,
} from './utils/toolbar-controller-ref-register';

// This stylesheet contains global styles so must be imported even if not used
// eslint-disable-next-line no-unused-vars
import styles from './DraftEditor.less';

const DEFAULT_TOOLBAR_OPTIONS = Object.freeze(
  ['inline', 'fontSize', 'list', 'colorPicker', 'image', 'embedded', 'emoji', 'history'].reduce(
    (acc, optionName) => {
      acc[optionName] = true;
      return acc;
    },
    {}
  )
);

const DEFAULT_TOOLBAR_FEATURES = Object.freeze(
  Object.entries({
    inline: ['bold', 'italic', 'underline', 'strikethrough'],
    list: ['unordered', 'ordered', 'indent', 'outdent'],
    link: ['link', 'unlink'],
    history: ['undo', 'redo'],
    colorPicker: [],
    image: [],
    emoji: [],
    fontSize: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 30, 36, 48, 60, 72, 96],
  }).reduce((acc, [optionName, features]) => {
    acc[optionName] = features.reduce((featureMap, f) => {
      featureMap[f] = true;
      return featureMap;
    }, {});

    return acc;
  }, {})
);

const TOOLBAR_FEATURES = Object.freeze({
  inline: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
  list: ['unordered', 'ordered', 'indent', 'outdent'],
  link: ['link', 'unlink'],
  history: ['undo', 'redo'],
  textAlign: ['left', 'center', 'right', 'justify'],
  colorPicker: null,
  image: null,
  emoji: null,
  fontSize: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 30, 36, 48, 60, 72, 96],
});

const TOOLBAR_FEATURES_CONFIG = Object.freeze({
  inline: {
    bold: customizeFeature('bold'),
    italic: customizeFeature('italic'),
    underline: customizeFeature('underline'),
    strikethrough: customizeFeature('strikethrough'),
  },
  list: {
    ordered: customizeFeature('ordered'),
    unordered: customizeFeature('unordered'),
    indent: customizeFeature('indent'),
    outdent: customizeFeature('outdent'),
  },
  history: {
    undo: customizeFeature('undo'),
    redo: customizeFeature('redo'),
  },
  colorPicker: customizeFeature('color-picker'),
  image: customizeFeature('image'),
  emoji: customizeFeature('emoji'),
});

const TAB_STR = '　　';

const VideoUploadComponent = ({ onChange, videoUploadPreset }) => {
  useScript('https://widget.cloudinary.com/v2.0/global/all.js');

  const showUploadWidget = () => {
    // cloudinary class is loaded via script
    // eslint-disable-next-line no-undef
    cloudinary.openUploadWidget(
      {
        uploadPreset: videoUploadPreset,
        ...UPLOAD_WIDGET_CONFIG,
      },
      (err, result) => {
        if (err) {
          showErrorNotification(err);
          console.error(err);
          return;
        }

        if (result.event === 'success') {
          onChange(result.info.secure_url, '360', '560');
        }
      }
    );
  };

  const feature = customizeFeature('video');

  return (
    <div className={classNames('rdw-option-wrapper', feature.className)} onClick={showUploadWidget}>
      <img alt src={feature.icon} />
    </div>
  );
};

VideoUploadComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
  videoUploadPreset: PropTypes.string.isRequired,
};

export const BaseDraftEditor = (props) => {
  const {
    value,
    onChange,
    readOnly,
    defaultFontSize,
    toolbarCustomOptions,
    toolbarCustomFeatures,
    toolbarCustomButtons,
    wrapperClassName,
    stripPastedStyles,
    videoUploadPreset,
    placeholder,
    toolbarOnFocus,
  } = props;

  const [editorRef, setEditorRef] = useState();
  const [editorState, setEditorState] = useState(value);
  const [toolbarHidden, setToolbarHidden] = useState(toolbarOnFocus);

  const containerRef = useRef();
  const isInCompositionRef = useRef(false);
  const didMount = useRef(false);

  const toolbarControllerRef = useRef({
    hideToolbar: () => {
      setToolbarHidden(true);
    },
  });

  const uploadImageCallBack = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject('Invalid file.');
      }

      const upload = uploadToCloudinary(file);
      upload.end((err, response) => {
        if (err) reject(err);
        const res = {
          data: {
            link: response.body.secure_url,
            type: 'image/jpeg',
            ...response.body,
          },
          status: 200,
          success: true,
        };

        resolve(res);
      });
    });
  };

  const handleEditorStateChange = (editorState) => {
    const isInComposition = isInCompositionRef.current;

    if (isInComposition) {
      return;
    }

    setEditorState(editorState);
    onChange(editorState);
  };

  const handleTabKey = (e) => {
    e.preventDefault();

    const { editorState } = editorRef.props;

    const currentContent = editorState.getCurrentContent();
    const currentSelection = editorState.getSelection();

    const newContent = Modifier.insertText(currentContent, currentSelection, TAB_STR);

    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
    handleEditorStateChange(newEditorState);
  };

  const EmbeddedVideoComponent = ({ onChange }) => (
    <VideoUploadComponent onChange={onChange} videoUploadPreset={videoUploadPreset} />
  );

  const toolbarOptions = Object.entries({
    ...DEFAULT_TOOLBAR_OPTIONS,
    ...toolbarCustomOptions,
  }).reduce((acc, [optionName, optionEnabled]) => {
    if (optionEnabled) {
      acc.push(optionName);
    }

    return acc;
  }, []);

  const toolbarFeatures = getToolbarFeatures(toolbarCustomFeatures);

  const handleOnFocus = () => {
    if (!toolbarOnFocus) return;

    forEachController((controllerRef) => {
      controllerRef.current.hideToolbar();
    });

    setToolbarHidden(false);
  };

  useEffect(() => {
    if (!toolbarOnFocus) return;

    registerController(toolbarControllerRef);

    return () => {
      unregisterController(toolbarControllerRef);
    };
  }, []);

  useEffect(() => {
    const editorEl = containerRef.current.querySelectorAll('[contenteditable="true"]')[0];

    if (!editorEl) {
      return;
    }

    const handleCompositionStart = () => {
      isInCompositionRef.current = true;
    };

    const handleCompositionEnd = () => {
      isInCompositionRef.current = false;
    };

    editorEl.addEventListener('compositionstart', handleCompositionStart);
    editorEl.addEventListener('compositionend', handleCompositionEnd);

    return () => {
      editorEl.removeEventListener('compositionstart', handleCompositionStart);
      editorEl.removeEventListener('compositionend', handleCompositionEnd);
    };
  }, []);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    handleEditorStateChange(value);
  }, [value]);

  const hasListContent = useMemo(() => {
    const contentState = editorState.getCurrentContent();
    return !contentState.hasText() && contentState.getBlockMap().first().getType() !== 'unstyled';
  }, [editorState.getCurrentContent()]);

  return (
    <div ref={containerRef} style={{ fontSize: defaultFontSize }}>
      <Editor
        editorRef={setEditorRef}
        readOnly={readOnly}
        editorState={editorState}
        stripPastedStyles={stripPastedStyles}
        wrapperClassName={classNames(wrapperClassName, styles.meeoppEditorWrapper)}
        editorClassName={classNames(styles.meeoppEditor, {
          [styles.editorWithoutPlaceholder]: hasListContent,
        })}
        onEditorStateChange={handleEditorStateChange}
        onTab={handleTabKey}
        toolbar={{
          options: toolbarOptions,
          ...toolbarFeatures,
          image: {
            ...toolbarFeatures.image,
            uploadCallback: uploadImageCallBack,
            previewImage: true,
            alt: { present: false, mandatory: false },
            defaultSize: {
              width: '100%',
            },
          },
          embedded: {
            ...toolbarFeatures.embedded,
            component: EmbeddedVideoComponent,
          },
        }}
        toolbarCustomButtons={toolbarCustomButtons}
        placeholder={placeholder}
        onFocus={handleOnFocus}
        toolbarHidden={toolbarHidden}
      />
    </div>
  );
};

BaseDraftEditor.propTypes = {
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  defaultFontSize: PropTypes.number,
  toolbarCustomOptions: PropTypes.object,
  toolbarCustomFeatures: PropTypes.object,
  toolbarCustomButtons: PropTypes.array,
  wrapperClassName: PropTypes.string,
  stripPastedStyles: PropTypes.bool,
  videoUploadPreset: PropTypes.string,
  placeholder: PropTypes.string,
  toolbarOnFocus: PropTypes.bool,
};

BaseDraftEditor.defaultProps = {
  onChange: () => null,
  initial: null,
  defaultFontSize: 24,
  toolbarCustomOptions: {},
  toolbarCustomFeatures: {},
  toolbarCustomButtons: [],
  stripPastedStyles: true,
  toolbarOnFocus: false,
};

const getToolbarFeatures = (toolbarCustomFeatures) => {
  return Object.keys(DEFAULT_TOOLBAR_FEATURES).reduce((acc, optionName) => {
    acc[optionName] = {
      ...TOOLBAR_FEATURES_CONFIG[optionName],
    };

    const options =
      TOOLBAR_FEATURES[optionName] &&
      TOOLBAR_FEATURES[optionName].filter((f) => {
        return (
          toolbarCustomFeatures[optionName]?.[f] ?? !!DEFAULT_TOOLBAR_FEATURES[optionName]?.[f]
        );
      });

    if (options) {
      acc[optionName].options = options;
    }

    return acc;
  }, {});
};

export const DraftEditor = withPinyin(withAudio(BaseDraftEditor));
